import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useEditor, EditorContent, Editor as TipTapEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import Highlight from '@tiptap/extension-highlight';
import { Save, Send, Loader2, Eye, FileText } from 'lucide-react';
import { EditorToolbar } from './EditorToolbar';
import { BlogPreview } from './BlogPreview';
import { BlogPost } from '../../types/blog';

export type BlogEditorMode = 'create' | 'edit';

/** The subset of a BlogPost this editor owns. Identity fields are passed through when editing. */
export type BlogEditorPayload = Pick<
  BlogPost,
  'title' | 'description' | 'content' | 'status' | 'tag'
> &
  Partial<Pick<BlogPost, '_id' | 'id'>>;

export interface BlogEditorProps {
  mode: BlogEditorMode;
  initialPost?: Partial<BlogPost>;
  /**
   * Persist a draft. Resolve true on success.
   * The caller owns user notifications (react-toastify) — the editor does not toast.
   */
  onSave?: (post: BlogEditorPayload) => Promise<boolean>;
  /** Persist and submit. Resolve true on success. */
  onSubmit?: (post: BlogEditorPayload) => Promise<boolean>;
  loading?: boolean;
  readOnly?: boolean;
}

const EditorSkeleton = () => (
  <div className="animate-pulse space-y-6">
    <div className="h-12 bg-gray-200 rounded-lg"></div>
    <div className="h-8 bg-gray-200 rounded w-1/3"></div>
    <div className="space-y-3">
      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
      <div className="h-4 bg-gray-200 rounded w-5/6"></div>
    </div>
    <div className="h-64 bg-gray-200 rounded-lg"></div>
  </div>
);

const FIELD_CLASSES =
  'w-full rounded-xl border-2 border-gray-300 bg-white shadow-sm transition-colors duration-200 ' +
  'placeholder-gray-400 hover:border-gray-400 ' +
  'focus:border-purple-600 focus:ring-2 focus:ring-purple-200 focus:outline-none ' +
  'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed';

/**
 * BlogEditor — the single rich-text editor behind both blog creation and editing.
 *
 * It is purely presentational: it assembles a payload from its fields and hands it to
 * `onSave` / `onSubmit`, which own persistence. `mode` drives the handful of genuine
 * differences between creating and editing (copy and post-submit reset).
 *
 * Status is not author-editable: Save Draft always writes `draft`, Submit always
 * writes `pending_review`. Moving a post beyond review is a moderation action.
 */
export const BlogEditor = React.forwardRef<HTMLFormElement, BlogEditorProps>(({
  mode,
  initialPost,
  onSave,
  onSubmit,
  loading = false,
  readOnly = false,
}, ref) => {
  const isEditMode = mode === 'edit';

  // Form state
  const [title, setTitle] = useState(initialPost?.title || '');
  const [description, setDescription] = useState(initialPost?.description || '');
  const [tags, setTags] = useState<string[]>(initialPost?.tag || []);

  // UI state
  const [showPreview, setShowPreview] = useState(false);
  const [isEditorReady, setIsEditorReady] = useState(false);
  const [wordCount, setWordCount] = useState(0);

  // Loading states
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const editorContentRef = useRef(initialPost?.content || '');
  const internalFormRef = useRef<HTMLFormElement | null>(null);

  const extensions = useMemo(() => [
    StarterKit,
    Link.configure({
      openOnClick: false,
      HTMLAttributes: {
        class: 'text-purple-600 hover:text-purple-800 underline',
      },
    }),
    Image.configure({
      HTMLAttributes: {
        class: 'max-w-full h-auto rounded-lg shadow-md',
      },
    }),
    Placeholder.configure({
      placeholder: isEditMode
        ? 'Edit your blog post content...'
        : 'Start writing your amazing blog post... ✨',
      showOnlyWhenEditable: true,
    }),
    Highlight.configure({
      HTMLAttributes: {
        class: 'bg-yellow-200 px-1 rounded',
      },
    }),
  ], [isEditMode]);

  const editor = useEditor({
    extensions,
    content: initialPost?.content || '',
    editable: !readOnly,
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg max-w-none p-4 sm:p-6 lg:p-8 min-h-[350px] sm:min-h-[450px] lg:min-h-[500px] focus:outline-none bg-white rounded-lg transition-all duration-200',
        'aria-label': 'Blog content',
      },
    },
    onUpdate: ({ editor: instance }: { editor: TipTapEditor }) => {
      editorContentRef.current = instance.getHTML();
      const text = instance.getText();
      setWordCount(text.trim() ? text.trim().split(/\s+/).length : 0);
    },
  });

  // Mark ready once TipTap has mounted, and seed the cached content / word count.
  useEffect(() => {
    if (editor && !isEditorReady) {
      setIsEditorReady(true);
      editorContentRef.current = editor.getHTML();
      const text = editor.getText();
      setWordCount(text.trim() ? text.trim().split(/\s+/).length : 0);
    }
  }, [editor, isEditorReady]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!readOnly);
    }
  }, [editor, readOnly]);

  // Re-sync every field when a fresh post arrives (e.g. the edit page finishes fetching).
  useEffect(() => {
    if (!initialPost || !editor || !isEditorReady) return;

    setTitle(initialPost.title || '');
    setDescription(initialPost.description || '');
    setTags(initialPost.tag || []);

    if (initialPost.content && initialPost.content !== editorContentRef.current) {
      editor.commands.setContent(initialPost.content);
      editorContentRef.current = initialPost.content;
    }
  }, [initialPost, editor, isEditorReady]);

  useEffect(() => {
    return () => {
      if (editor) {
        editor.destroy();
      }
    };
  }, [editor]);

  const buildPayload = useCallback((nextStatus: BlogPost['status']): BlogEditorPayload => {
    const payload: BlogEditorPayload = {
      title: title.trim(),
      description: description.trim(),
      content: editorContentRef.current || editor?.getHTML() || '',
      status: nextStatus,
      tag: tags,
    };

    if (isEditMode) {
      payload._id = initialPost?._id;
      payload.id = initialPost?.id;
    }

    return payload;
  }, [title, description, editor, tags, isEditMode, initialPost?._id, initialPost?.id]);

  const resetForm = useCallback(() => {
    setTitle('');
    setDescription('');
    setTags([]);
    editor?.commands.clearContent();
    editorContentRef.current = '';
    setWordCount(0);
    internalFormRef.current?.reset();
  }, [editor]);

  // The buttons show their own spinner + disabled state while these run, and the
  // page toasts the outcome. The editor deliberately stays silent.
  const handleSave = useCallback(async () => {
    if (!editor || !title.trim() || isSaving || !onSave) return;

    setIsSaving(true);
    try {
      await onSave(buildPayload('draft'));
    } catch (error) {
      console.error('Save failed:', error);
    } finally {
      setIsSaving(false);
    }
  }, [editor, title, isSaving, onSave, buildPayload]);

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!editor || !title.trim() || isSubmitting || !onSubmit) return;

    setIsSubmitting(true);
    try {
      // Submitting always hands the post to review — authors do not set status.
      const success = await onSubmit(buildPayload('pending_review'));
      if (success && !isEditMode) {
        resetForm();
      }
    } catch (error) {
      console.error('Submit failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [editor, title, isSubmitting, onSubmit, isEditMode, buildPayload, resetForm]);

  const previewContent = useMemo(() => ({
    title,
    content: editorContentRef.current || editor?.getHTML() || '',
    tags,
  }), [title, editor, tags]);

  const isFormDisabled = loading || isSaving || isSubmitting || readOnly;
  const canSave = !isFormDisabled && title.trim() !== '' && !!onSave;
  const canSubmit = !isFormDisabled && title.trim() !== '' && !!onSubmit;

  const setFormRef = useCallback((node: HTMLFormElement | null) => {
    internalFormRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  }, [ref]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8 min-h-[400px] bg-white rounded-xl">
        <div className="flex items-center space-x-3">
          <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
          <span className="text-lg font-medium text-gray-700">Loading blog post...</span>
        </div>
      </div>
    );
  }

  if (!isEditorReady) {
    return (
      <div className="bg-white rounded-xl p-6 sm:p-8">
        <div className="flex items-center justify-center mb-6">
          <div className="flex items-center space-x-3">
            <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
            <span className="text-lg font-medium text-gray-700">Initializing editor...</span>
          </div>
        </div>
        <EditorSkeleton />
      </div>
    );
  }

  if (showPreview) {
    return (
      <BlogPreview
        {...previewContent}
        onClose={() => setShowPreview(false)}
      />
    );
  }

  return (
    <div className="max-w-none mx-auto bg-white rounded-xl shadow-sm border border-gray-200">
      {readOnly && (
        <div className="bg-yellow-100 text-yellow-800 px-4 py-3 rounded-t-xl font-medium text-center">
          This blog is read-only because it is published or pending review.
        </div>
      )}

      <form ref={setFormRef} onSubmit={handleSubmit} className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8">
        {/* Header fields */}
        <div className="border-b border-gray-200 pb-6 space-y-5">
          <div>
            <label htmlFor="blog-title" className="block text-sm font-semibold text-gray-800 mb-2">
              Blog Title <span className="text-red-500" aria-hidden="true">*</span>
            </label>
            <input
              id="blog-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter your blog title..."
              required
              aria-required="true"
              disabled={isFormDisabled}
              className={`${FIELD_CLASSES} px-4 py-3 text-2xl sm:text-3xl font-bold text-gray-900`}
            />
          </div>

          <div>
            <label htmlFor="blog-description" className="block text-sm font-semibold text-gray-800 mb-2">
              Blog Description
            </label>
            <textarea
              id="blog-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter a brief description for your blog post..."
              rows={3}
              disabled={isFormDisabled}
              aria-describedby="blog-description-help"
              className={`${FIELD_CLASSES} px-4 py-3 text-base sm:text-lg text-gray-700 resize-y`}
            />
            <p id="blog-description-help" className="mt-1.5 text-xs text-gray-500">
              Optional — a short summary shown in blog listings.
            </p>
          </div>

          {/* Meta row */}
          <div className="flex items-center justify-end">
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <FileText className="h-4 w-4" aria-hidden="true" />
              <span>{wordCount} words</span>
              <span>•</span>
              <span>{Math.ceil(wordCount / 250)} min read</span>
            </div>
          </div>
        </div>

        {/* Formatting toolbar */}
        <div>
          {editor && <EditorToolbar editor={editor} disabled={isFormDisabled} />}
        </div>

        {/* Editor body */}
        <div className="border-2 border-gray-200 rounded-xl overflow-hidden shadow-sm hover:border-purple-300 transition-colors duration-200 bg-gradient-to-br from-white to-gray-50/50">
          {editor && <EditorContent editor={editor} />}
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-3">
          <button
            type="button"
            onClick={() => setShowPreview(true)}
            disabled={isFormDisabled}
            className="group inline-flex items-center justify-center px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <Eye className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform" aria-hidden="true" />
            Preview
          </button>

          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="group inline-flex items-center justify-center px-4 py-2.5 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 transition-all duration-200 shadow-sm hover:shadow-md"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform" aria-hidden="true" />
                Save Draft
              </>
            )}
          </button>

          <button
            type="submit"
            disabled={!canSubmit}
            className="group inline-flex items-center justify-center px-4 py-2.5 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1 transition-all duration-200 shadow-sm hover:shadow-md"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />
                {isEditMode ? 'Updating...' : 'Submitting...'}
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform" aria-hidden="true" />
                {isEditMode ? 'Update Post' : 'Submit Post'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
});

BlogEditor.displayName = 'BlogEditor';
